import React, { FunctionComponent, ReactNode } from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { CloseCircleBordered } from '../../atoms/icons/CloseCircleBordered'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { Title } from '../../atoms/title/Title'
import { Tooltip } from '../../atoms/tooltip/Tooltip'
import { TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export type MessageAttachmentProps = {
  onPress?: () => void
  onClosePress?: () => void
  showClose?: boolean
  text: string
  icon: ReactNode
  closeTooltipLabel?: ReactNode
  disabled?: boolean
  disabledTooltipContent?: ReactNode
}

const Container = styled.View({
  flexDirection: 'row',
  alignSelf: 'flex-start',
})
const InnerContainer = styled(PressableOpacity)<{ theme: ThemeType; disabled?: boolean }>(({ theme, disabled }) => ({
  flexDirection: 'row',
  backgroundColor: theme.colors.components.messageAttachment.innerContainer.background,
  alignItems: 'center',
  padding: theme.spacing['12px'],
  borderRadius: '16px',
  minWidth: '215px',
  opacity: disabled ? 0.5 : 1,
}))

const IconContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.backgroundPrimary,
  padding: theme.spacing['8px'],
  marginRight: theme.spacing['12px'],
  borderRadius: '8px',
}))

const CloseTooltip = styled(Tooltip)({
  position: 'absolute',
  top: '-8px',
  right: '-8px',
})

export const MessageAttachment: FunctionComponent<MessageAttachmentProps> = ({
  onPress,
  text,
  icon,
  onClosePress = () => {},
  showClose = false,
  closeTooltipLabel,
  disabled = false,
  disabledTooltipContent,
}) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme()

  const innerContent = (
    <InnerContainer
      accessible
      accessibilityRole='button'
      accessibilityLabel={text}
      testID={tID('MessageAttachment-button')}
      onPress={disabled ? undefined : onPress}
      disabled={disabled}
    >
      <IconContainer>{icon}</IconContainer>
      <Title size={TitleSize.SMALL} color={colors.textInverse} text={text} />
    </InnerContainer>
  )

  return (
    <Container>
      {disabled && disabledTooltipContent ? (
        <Tooltip hoverEnabled content={disabledTooltipContent} placement='top'>
          {innerContent}
        </Tooltip>
      ) : (
        innerContent
      )}
      {showClose && (
        <CloseTooltip
          testID={tID('MessageAttachment-close-button')}
          hoverEnabled
          content={closeTooltipLabel}
          placement='top'
          onPress={onClosePress}
          accessibilityLabel={formatMessage({
            defaultMessage: 'Remove attachment',
            description: 'Remove attachment',
          })}
        >
          <CloseCircleBordered />
        </CloseTooltip>
      )}
    </Container>
  )
}
